import type {Component} from "vue";
import {DxPopup} from "devextreme-vue/popup";
import {messageBoxResult, type messageBoxButtons, type messageBoxIcon} from "../components/dialogs/message-box";
import MessageBox from "../components/dialogs/MessageBox.vue";
import InputBox from "../components/dialogs/InputBox.vue";
import type { DxTextArea, DxTextBox } from "devextreme-vue";


type OnModalShownCallback = (request: {
    component: Component,
    props?: Record<string, unknown>,
    popupProps?: Omit<DxPopup, "toolbarItems">,
    promiseResolver: (value: unknown) => void
}) => void;

let instance: ReturnType<typeof useModalServiceInternal> | null;
const defaultPopupOptions: Omit<DxPopup, "toolbarItems"> = {dragEnabled: false, hideOnOutsideClick: false};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useModalServiceInternal() {

    let onModalShownCallback: OnModalShownCallback | undefined;

    function showModal(component: Component, props?: Record<string, unknown>, popupProps?: Omit<DxPopup, "toolbarItems">): Promise<unknown> {
        return new Promise((resolve, reject) => {
            if (onModalShownCallback)
                onModalShownCallback({component, props, popupProps: {...popupProps, ...defaultPopupOptions}, promiseResolver: resolve})
            else
                reject();
        });
    }

    function messageBox(content: string, title: string, buttons: messageBoxButtons, icon: messageBoxIcon, isHtml?: boolean, width?: number, height?: number): Promise<unknown> {
        return showModal(MessageBox, {content, buttons, icon, isHtml}, {
            width: width ?? "500", height: height ?? "auto", title: title, showTitle: true, showCloseButton: false
        });
    }

    async function inputBox(
        label: string,
        dxInputType: 'dxTextBox' | 'dxTextArea',
        dxInputProps?: any,
        required?: boolean,
        showCancel = true,
    ): Promise<string | undefined> {
        const popUpProps: Omit<DxPopup, "toolbarItems" | "showCloseButton"> = {
            width: "500", height: dxInputType === 'dxTextBox' ? "200" : "auto", showTitle: false
        };

        return showModal(InputBox, { label, dxInputType, dxInputProps, required, showCancel }, popUpProps).then((result: any) => {
            if (result.result === messageBoxResult.ok) {
                return result.value;
            }
            return undefined;
        });
    }

    function onModalShowRequest(callback: OnModalShownCallback) : void {
        onModalShownCallback = callback;
    }

    // noinspection JSUnusedGlobalSymbols
    return {
        showModal,
        messageBox,
        inputBox,
        onModalShowRequest: onModalShowRequest
    }
}

export function useModalService() : ReturnType<typeof useModalServiceInternal> {
    if (instance == null)
        instance = useModalServiceInternal();
    return instance;
}